import classNames from 'classnames/bind'
import React, { useEffect, useRef, useCallback } from 'react'
import { Portal } from '../Portal'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const css = require('./Overlay.scss')
const cx = classNames.bind(css)

const ESCAPE = 'Escape'

/*
  ### = the Overlay component
 ┌────────────────────────────────────────────────┐
 │################################################│
 │################################################│
 │###################┌────────┐###################│
 │###################│ Button │###################│
 │###################└────────┘###################│
 │########┌──────────────────────────────┐########│
 │########│         Open Dropdown        │########│
 │########└──────────────────────────────┘########│
 │################################################│
 │################################################│
 └────────────────────────────────────────────────┘
*/

interface OverlayProps {
  /**
   * Content to render in the overlay
   */
  children: React.ReactNode
  /**
   * Toggles visibility of the overlay (and children). This is a controlled component,
   * so this prop is required to show/hide the overlay
   */
  isOpen: boolean
  /**
   * Should a full-width backdrop be rendered behind the contents?
   * @default false
   */
  hasBackdrop?: boolean
  /**
   * Function that is called when the overlay intends to close (since it is controlled
   * by the isOpen prop it does not actually close the overlay)
   * @default noop
   */
  onClose?: (event: React.SyntheticEvent<HTMLElement>) => void
}

/**
 * Utility component that allows rendering of content on top of its siblings or the entire application.
 * Helps handle focus moving.
 *
 * Handles clicks outside of the component and optionaly provides a dimming background which prevents interaction with
 * anything behind it.
 */
export function Overlay({
  children,
  hasBackdrop = false,
  isOpen,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose: propsOnClose = () => {},
}: OverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const containsTarget = (target: HTMLElement) =>
    containerRef.current &&
    containerRef.current.contains(target) &&
    !containerRef.current.isSameNode(target)
  const onClose = useCallback(propsOnClose, [propsOnClose])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const hasTarget = containsTarget(target)

      if (isOpen && !hasTarget && onClose) {
        onClose((e as unknown) as React.SyntheticEvent<HTMLElement>)
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ESCAPE && onClose) {
        onClose((e as unknown) as React.SyntheticEvent<HTMLElement>)
        e.preventDefault()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClick)
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, hasBackdrop, onClose])

  /* Handle moving focus inside of the overlay */
  useEffect(() => {
    if (
      containerRef.current === null ||
      document.activeElement === null ||
      !isOpen
    ) {
      return
    }

    const isFocusOutside =
      containerRef.current &&
      !containerRef.current.contains(document.activeElement)
    if (isFocusOutside) {
      // find element to focus
      const autofocusElement = containerRef.current.querySelector<HTMLElement>(
        '[autofocus]'
      )
      const wrapperElement = containerRef.current.querySelector<HTMLElement>(
        '[tabindex]'
      )

      if (autofocusElement !== null) {
        autofocusElement.focus()
      } else if (wrapperElement !== null) {
        wrapperElement.focus()
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Portal>
      <div
        ref={containerRef}
        className={cx('overlay', { 'overlay-backdrop': hasBackdrop })}
      >
        {children}
      </div>
    </Portal>
  )
}
